// buildInfo.ts — identifies WHICH build is running, so a bug report can say
// «dev, v1.0.0 (42), ios» instead of «на телефоне».
//
// Sources differ per platform and that's fine — each gives the id that is
// actually unique there:
//   • native (EAS): app version + native build number. eas.json uses
//     appVersionSource=remote + autoIncrement, so the build number is assigned
//     by EAS and maps 1:1 to a build in the EAS dashboard.
//   • web (Vercel): the commit SHA / branch that scripts/inject-build-env.js
//     bakes into the bundle as EXPO_PUBLIC_COMMIT_SHA / EXPO_PUBLIC_GIT_BRANCH.
//
// Pairs with config/envSwitch.ts: env answers «против какого бэка», this answers
// «какая сборка».
import { Platform } from 'react-native';

import { BUILD_SHA, BUILD_BRANCH } from './buildStamp';

// buildStamp.ts FIRST, the injected env var only as a fallback. Both are written
// by scripts/inject-build-env.js, but `process.env.EXPO_PUBLIC_*` is inlined by
// babel at transform time and is not part of metro's cache key — on our shared
// runner cache a rebuilt tree gets served the previous build's constant. Seen on
// the dev stand 29.07: the chip reported 1255ec5 / branch `ci/second-runner-pool`
// while the deployed bundle was a00115a8 on `dev`. A build id that names the
// wrong commit is worse than none, so read the generated module, which changes
// content (and therefore cache key) with every build. Same order App.tsx already
// uses for the Sentry release.
const COMMIT_SHA = (BUILD_SHA || process.env.EXPO_PUBLIC_COMMIT_SHA || '').trim();
const GIT_BRANCH = (BUILD_BRANCH || process.env.EXPO_PUBLIC_GIT_BRANCH || '').trim();

export interface BuildParts {
  /** Marketing version, e.g. '1.0.0'. */
  version: string;
  /** iOS CFBundleVersion / Android versionCode. Empty on web. */
  buildNumber: string;
  /** Short commit SHA. Empty on native (not injected there) and on local web. */
  sha: string;
  /** Git branch of the deploy. Empty outside Vercel builds. */
  branch: string;
  platform: string;
}

// expo-application is already autolinked (expo-auth-session depends on it), so
// reading the native build number costs no new native module. Required lazily
// and guarded: on web / in jest the module may be a stub or absent.
function nativeApplication(): { version?: string | null; build?: string | null } {
  try {
    const Application = require('expo-application');
    return {
      version: Application?.nativeApplicationVersion ?? null,
      build: Application?.nativeBuildVersion ?? null,
    };
  } catch {
    return {};
  }
}

function configVersion(): string {
  try {
    const Constants = require('expo-constants').default;
    return Constants?.expoConfig?.version || '';
  } catch {
    return '';
  }
}

export function buildParts(): BuildParts {
  const native = Platform.OS === 'web' ? {} : nativeApplication();
  return {
    version: (native.version || configVersion() || '').toString(),
    buildNumber: (native.build || '').toString(),
    sha: COMMIT_SHA ? COMMIT_SHA.slice(0, 7) : '',
    branch: GIT_BRANCH,
    platform: Platform.OS,
  };
}

/** Compact one-liner for the on-screen chip, e.g. `v1.0.0 (42)` / `v1.0.0 · a1b2c3d`. */
export function buildLabel(): string {
  const { version, buildNumber, sha } = buildParts();
  const head = version
    ? `v${version}${buildNumber ? ` (${buildNumber})` : ''}`
    : buildNumber
      ? `build ${buildNumber}`
      : '';
  return [head, sha].filter(Boolean).join(' · ') || 'локальная сборка';
}

/** Full line to paste into a bug report, e.g. `dev · ios · v1.0.0 (42)`. */
export function buildReport(env: string): string {
  const { platform, branch } = buildParts();
  return [env, platform, buildLabel(), branch].filter(Boolean).join(' · ');
}
